import { useEffect, useRef, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { DataTableViewData } from '@shared/types/views';
import { RowCrmAction } from '../atoms/RowCrmAction';
import { buildRowDetail } from './row-detail-model';
import { DetailText, DocumentCard, StatusPill } from './RowDetailParts';

type Row = DataTableViewData['rows'][number];
type RowPosition = { index: number; total: number };

interface RowDetailProps {
  row: Row;
  columnLabels: Record<string, string>;
  /** The table's quick filter column: its value reads as the row's decision. */
  statusKey: string | null;
  /** Place of the row among the rows the table shows (its filter and sort). */
  position: RowPosition | null;
  onBack: () => void;
  onMove: (step: -1 | 1) => void;
  /** Live run result: the row can be added to the CRM. */
  crm: { sourceRunId: string; autoStart: boolean; onStarted: () => void } | null;
}

const NAV_BUTTON = 'flex h-8 w-8 items-center justify-center rounded-full text-domino-aubergine transition hover:bg-domino-plumTint disabled:cursor-default disabled:text-domino-cardBorder disabled:hover:bg-transparent';

const isTyping = (target: EventTarget) => target instanceof HTMLElement
  && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/** Previous and next row, for the drawer's header. */
export const RowNav = ({ position, onMove }: { position: RowPosition | null; onMove: (step: -1 | 1) => void }) => (
  position && position.total > 1 ? (
    <div className="flex shrink-0 items-center gap-1 text-xs text-domino-inkSoft">
      <button type="button" className={NAV_BUTTON} aria-label="Résultat précédent" disabled={position.index === 0} onClick={() => onMove(-1)}>
        <ChevronLeft aria-hidden="true" className="h-4 w-4" />
      </button>
      <span className="tabular-nums">{position.index + 1} sur {position.total}</span>
      <button type="button" className={NAV_BUTTON} aria-label="Résultat suivant" disabled={position.index === position.total - 1} onClick={() => onMove(1)}>
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  ) : null
);

/**
 * The fiche of one row, shown in a drawer beside the table: the row's values
 * arranged by their shape. Arrows move to the previous and next row.
 */
export const RowDetail = ({ row, columnLabels, statusKey, position, onBack, onMove, crm }: RowDetailProps) => {
  const ref = useRef<HTMLElement>(null);
  const model = buildRowDetail(row, columnLabels, statusKey);

  // Moved to another row: the drawer starts again at its top.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!element.contains(document.activeElement)) element.focus({ preventScroll: true });
    element.scrollIntoView?.({ block: 'start' });
  }, [row.id]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // Dialogs opened from the fiche are portals: their keys are theirs.
    if (event.defaultPrevented || !event.currentTarget.contains(event.target as Node) || isTyping(event.target)) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'Escape') onBack();
    else if (event.key === 'ArrowLeft' && position && position.index > 0) onMove(-1);
    else if (event.key === 'ArrowRight' && position && position.index < position.total - 1) onMove(1);
    else return;
    event.preventDefault();
  };

  const twoColumns = model.paragraphs.length > 0 && model.documents.length > 0;
  return (
    <section ref={ref} tabIndex={-1} onKeyDown={onKeyDown} aria-label="Fiche détaillée"
      className="min-h-full bg-domino-white focus:outline-none">
      <header className="bg-domino-plumTint/70 px-4 pb-5 pt-5 cq-sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
          <div className="min-w-0">
            <h3 className="font-display text-2xl font-semibold leading-tight tracking-tight text-domino-ink">{model.title}</h3>
            {model.identifier && (
              <p className="mt-1 text-xs text-domino-inkSoft">{model.identifier.label} · <span className="font-mono">{model.identifier.value}</span></p>
            )}
          </div>
          {crm && model.title !== '—' && (
            <RowCrmAction key={row.id} sourceRunId={crm.sourceRunId} rowId={row.id} rowLabel={model.title}
              autoStart={crm.autoStart} onStarted={crm.onStarted} className="mt-0.5" />
          )}
        </div>
        {model.statuses.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {model.statuses.map((field) => <StatusPill key={field.key} field={field} />)}
          </div>
        )}
        {model.facts.length > 0 && (
          <dl className="mt-4 grid gap-2 cq-sm:grid-cols-2 cq-lg:grid-cols-4">
            {model.facts.map((field) => (
              <div key={field.key} className="rounded-xl bg-domino-white px-3 py-2 ring-1 ring-domino-aubergine/10">
                <dt className="text-xs text-domino-inkSoft">{field.label}</dt>
                <dd className="mt-0.5 break-words text-sm font-medium leading-6 text-domino-ink"><DetailText text={field.text} /></dd>
              </div>
            ))}
          </dl>
        )}
        {model.empty.length > 0 && (
          <p className="mt-3 text-xs text-domino-inkSoft">Sans valeur : {model.empty.join(', ')}</p>
        )}
      </header>

      {(model.paragraphs.length > 0 || model.documents.length > 0) && (
        <div className={twoColumns ? 'grid gap-6 px-4 py-5 cq-sm:px-6 cq-lg:grid-cols-2' : 'px-4 py-5 cq-sm:px-6'}>
          {model.paragraphs.length > 0 && (
            <dl className={twoColumns ? 'space-y-5' : 'grid gap-x-8 gap-y-5 cq-lg:grid-cols-2'}>
              {model.paragraphs.map((field) => (
                <div key={field.key}>
                  <dt className="flex items-center gap-2 text-sm font-semibold text-domino-aubergine">
                    <span aria-hidden="true" className="h-3.5 w-1 rounded-full bg-domino-sun" />
                    {field.label}
                  </dt>
                  <dd className="mt-1.5 max-w-prose break-words text-[0.9375rem] leading-7 text-domino-ink"><DetailText text={field.text} /></dd>
                </div>
              ))}
            </dl>
          )}
          {model.documents.length > 0 && (
            <div className={twoColumns ? 'space-y-4' : 'grid gap-4 cq-lg:grid-cols-2'}>
              {model.documents.map((field) => <DocumentCard key={`${row.id}\u0000${field.key}`} field={field} />)}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
